import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './TowerOfHanoi.css';

const TowerOfHanoi = () => {
  const [towers, setTowers] = useState([]);
  const [selectedTower, setSelectedTower] = useState(null);
  const [selectedDisk, setSelectedDisk] = useState(null);
  const [moves, setMoves] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [diskCount, setDiskCount] = useState(3);
  const [invalidMove, setInvalidMove] = useState(null);

  const initializeGame = (numDisks) => {
    const tower1 = Array.from({ length: numDisks }, (_, i) => numDisks - i);
    const tower2 = [];
    const tower3 = [];
    
    setTowers([tower1, tower2, tower3]);
    setSelectedTower(null);
    setSelectedDisk(null);
    setMoves(0);
    setIsComplete(false);
    setDiskCount(numDisks);
    setInvalidMove(null);
  };

  const handleTowerClick = (towerIndex) => {
    if (isComplete) return;

    if (selectedTower === null) {
      // Select a disk from this tower
      if (towers[towerIndex].length > 0) {
        setSelectedTower(towerIndex);
        setSelectedDisk(towers[towerIndex][towers[towerIndex].length - 1]);
      }
    } else {
      // Try to move the selected disk to this tower
      if (towerIndex === selectedTower) {
        // Deselect if clicking the same tower
        setSelectedTower(null);
        setSelectedDisk(null);
      } else {
        // Attempt to move disk
        const newTowers = towers.map(tower => [...tower]);
        const fromTower = newTowers[selectedTower];
        const toTower = newTowers[towerIndex];
        
        // Check if move is valid
        if (toTower.length === 0 || fromTower[fromTower.length - 1] < toTower[toTower.length - 1]) {
          // Valid move
          const disk = fromTower.pop();
          toTower.push(disk);
          
          setTowers(newTowers);
          setMoves(prev => prev + 1);
          
          // Check for completion (all disks on tower 3)
          if (newTowers[2].length === diskCount) {
            setIsComplete(true);
          }
        } else {
          // Invalid move - show feedback
          setInvalidMove(towerIndex);
          setTimeout(() => setInvalidMove(null), 500);
        }
        
        // Reset selection
        setSelectedTower(null);
        setSelectedDisk(null);
      }
    }
  };

  const getDiskColor = (size) => {
    const colors = [
      '#e74c3c', // Red
      '#f39c12', // Orange
      '#f1c40f', // Yellow
      '#27ae60', // Green
      '#3498db', // Blue
      '#9b59b6', // Purple
      '#e67e22', // Dark Orange
      '#2ecc71'  // Light Green
    ];
    return colors[(size - 1) % colors.length];
  };

  const getDiskWidth = (size, maxSize) => {
    const minWidth = 80;
    const maxWidth = 180;
    return minWidth + ((maxWidth - minWidth) * (size - 1)) / (maxSize - 1);
  };

  const getMinimumMoves = (n) => {
    return Math.pow(2, n) - 1;
  };

  useEffect(() => {
    initializeGame(3);
  }, []);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🗼 Tower of Hanoi</h1>
        <div className="game-instructions">
          Move all disks from Tower A to Tower C. Only move one disk at a time, and never place a larger disk on a smaller one.
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {isComplete ? (
            `🎉 Puzzle Solved in ${moves} moves! ${moves === getMinimumMoves(diskCount) ? 'Perfect! 🌟' : `(Min: ${getMinimumMoves(diskCount)})`}`
          ) : (
            `Moves: ${moves} | Minimum: ${getMinimumMoves(diskCount)}`
          )}
        </div>

        <div className="difficulty-selector">
          <label>Disks:</label>
          {[3, 4, 5, 6, 7].map(num => (
            <button
              key={num}
              onClick={() => initializeGame(num)}
              className={`btn ${diskCount === num ? 'btn-primary' : 'btn-secondary'}`}
            >
              {num}
            </button>
          ))}
        </div>

        <div className="game-play-area">
          <div className="towers-container">
            {towers.map((tower, towerIndex) => (
              <div
                key={towerIndex}
                className={`tower ${selectedTower === towerIndex ? 'selected' : ''} ${
                  isComplete && towerIndex === 2 ? 'complete' : ''
                } ${invalidMove === towerIndex ? 'invalid-move' : ''}`}
                onClick={() => handleTowerClick(towerIndex)}
              >
                <div className="tower-label">
                  Tower {String.fromCharCode(65 + towerIndex)}
                </div>
                
                <div className="tower-pole">
                  <div className="pole"></div>
                  <div className="base"></div>
                  
                  <div className="disks">
                    {tower.map((diskSize, diskIndex) => (
                      <div
                        key={`${towerIndex}-${diskIndex}`}
                        className={`disk ${
                          selectedTower === towerIndex && diskIndex === tower.length - 1 
                            ? 'selected-disk' 
                            : ''
                        }`}
                        style={{
                          width: `${getDiskWidth(diskSize, diskCount)}px`,
                          backgroundColor: getDiskColor(diskSize),
                          bottom: `${25 + diskIndex * 28}px`
                        }}
                      >
                        {diskSize}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {selectedTower !== null && (
            <div className="move-hint">
              Click on another tower to move disk {selectedDisk}
            </div>
          )}
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={() => initializeGame(diskCount)}>
          Reset Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default TowerOfHanoi;